import { useId } from "react";
import type { CSSProperties, HTMLAttributes } from "react";
import "./ProgressBar.css";

type StyleWithVars = CSSProperties & Record<`--${string}`, string | number>;

export interface ProgressBarProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  "children"
> {
  /** Determinate progress, `0`–`1`. Values outside the range are clamped. */
  value: number;
  /** Visible label above the track. Also names the progress bar. */
  label?: string;
  /** Right-aligned percentage next to the label. Defaults to `true`. */
  showValue?: boolean;
  /** Used when there is no visible `label`. */
  "aria-label"?: string;
}

export function ProgressBar({
  value,
  label,
  showValue = true,
  className,
  ...rest
}: ProgressBarProps) {
  const uid = useId();
  const labelId = `${uid}label`;

  const clamped = Math.min(1, Math.max(0, Number.isFinite(value) ? value : 0));
  const percent = Math.round(clamped * 100);

  const classes = ["fc-progress", className]
    .filter((entry): entry is string => Boolean(entry))
    .join(" ");

  const fillStyle: StyleWithVars = { "--fc-progress-value": clamped };

  return (
    <div {...rest} className={classes}>
      {label !== undefined ? (
        <div className="fc-progress__header">
          <span className="fc-progress__label" id={labelId}>
            {label}
          </span>
          {showValue ? (
            <span className="fc-progress__value">{`${String(percent)}%`}</span>
          ) : null}
        </div>
      ) : null}

      <div
        className="fc-progress__track"
        role="progressbar"
        aria-valuenow={percent}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuetext={`${String(percent)}%`}
        aria-labelledby={label !== undefined ? labelId : undefined}
        aria-label={label === undefined ? rest["aria-label"] : undefined}
      >
        {/* scaleX, never width — §8.2 keeps this off the layout path. */}
        <span className="fc-progress__fill" style={fillStyle} />
      </div>
    </div>
  );
}
